import { action, Action, thunk, Thunk } from 'easy-peasy';
import axios from 'axios';
import { CommunicationRole, CommunicationRoleUser } from '~/types';
import { StoreModel } from '~/store';

type CommunicationRolesApiResponse = Array<{
    id: number
    name: string
    description?: string | null
    primaryName?: string | null
    userCount?: number | null
    users?: Array<{ id: number; name: string }>
}>;

export interface MachineConversation {
    chatId: number
    machineId: number
    machineIntern?: string
    projectNumber?: string
    categoryName?: string
    roleId: number
    roleName: string
    lastMessage: string
    lastMessageAt: string | null
    participantCount: number
    hasUnread: boolean
    userIds: number[]
    recipients: { id: number; name: string }[]
}

export interface CommunicationModel {
    roles: CommunicationRole[]
    setRoles: Action<CommunicationModel, CommunicationRole[]>
    conversations: MachineConversation[]
    setConversations: Action<CommunicationModel, MachineConversation[]>
    fetching: boolean
    setFetching: Action<CommunicationModel, boolean>

    fetchRoles: Thunk<CommunicationModel, { machineId: string | number; projectId: string | number }, any, StoreModel, Promise<void>>
    fetchConversations: Thunk<CommunicationModel, string | number, any, StoreModel, Promise<void>>
    fetchAllConversations: Thunk<CommunicationModel, void, any, StoreModel, Promise<void>>
    reset: Action<CommunicationModel>
}

const mapRoles = (data: CommunicationRolesApiResponse): CommunicationRole[] => {
    const list = Array.isArray(data) ? data : [];
    return list.map(row => {
        const users: CommunicationRoleUser[] = Array.isArray(row.users)
            ? row.users.map(u => ({ id: Number(u.id), name: u.name }))
            : [];
        return {
            id: Number(row.id),
            name: row.name,
            description: row.description ?? null,
            primaryName: row.primaryName ?? (users[0]?.name ?? null),
            userCount: Number(row.userCount ?? users.length),
            users,
        };
    });
};

export const communicationModel: CommunicationModel = {
    roles: [],
    setRoles: action((state, payload) => {
        state.roles = payload;
    }),
    conversations: [],
    setConversations: action((state, payload) => {
        state.conversations = payload;
    }),
    fetching: false,
    setFetching: action((state, payload) => {
        state.fetching = payload;
    }),

    fetchRoles: thunk(async (actions, { machineId, projectId }) => {
        actions.setFetching(true);
        try {
            const data = await axios.get<null, CommunicationRolesApiResponse>(
                '/communication/roles',
                { params: { machineId, projectId } }
            );
            actions.setRoles(mapRoles(data));
        } catch {
            actions.setRoles([]);
        } finally {
            actions.setFetching(false);
        }
    }),

    fetchConversations: thunk(async (actions, machineId) => {
        try {
            const data = await axios.get<null, MachineConversation[]>(
                '/machines/chat/conversations',
                { params: { machineId } }
            );
            actions.setConversations(Array.isArray(data) ? data : []);
        } catch {
            actions.setConversations([]);
        }
    }),

    fetchAllConversations: thunk(async (actions) => {
        try {
            const data = await axios.get<null, MachineConversation[]>(
                '/machines/chat/conversations/all'
            );
            actions.setConversations(Array.isArray(data) ? data : []);
        } catch {
            actions.setConversations([]);
        }
    }),

    reset: action((state) => {
        state.roles = [];
        state.conversations = [];
        state.fetching = false;
    }),
};

export const getInitials = (fullName: string): string => {
    const trimmed = (fullName || '').trim();
    if (!trimmed) {
        return '';
    }
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};
